import { Button, ButtonGroup, Card, IconButton, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { type ProjectComment, type Stored } from "@overmap-ai/common"
import { selectUserById } from "@overmap-ai/core"
import { Placeholder } from "@tiptap/extensions"
import { EditorContent, useEditor } from "@tiptap/react"
import { cx } from "class-variance-authority"
import { memo, useCallback } from "react"

import { textSizeCva } from "../../cva"
import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"
import { BASE_EXTENSIONS, LINK_NODES, MENTION_EXTENSIONS, useEditorContent, useEditorEditable } from "../../tiptap"
import { TextBubbleMenu } from "../../tiptap/components"
import { EditorContext } from "../../tiptap/context"
import { formatCompactDate } from "../../utils/date"
import { getUserDisplayLabel } from "../../utils/users"
import { ProjectCommentDropdownMenu } from "../DropdownMenus/ProjectCommentDropdownMenu"
import { UserIcon } from "../Icons/UserIcon"

interface ProjectCommentTimelineItemProps {
	projectComment: Stored<ProjectComment>
}

export const ProjectCommentTimelineItem = memo<ProjectCommentTimelineItemProps>((props) => {
	const { projectComment } = props

	const author = useAppSelector(selectUserById(projectComment.created_by ?? -1))
	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const editorContent = useEditorContent(projectComment.content)

	const editor = useEditor(
		{
			editorProps: {
				attributes: {
					class: "outline-none",
				},
			},
			content: editorContent,
			editable: false,
			extensions: [
				...BASE_EXTENSIONS,
				...LINK_NODES,
				...MENTION_EXTENSIONS,
				Placeholder.configure({
					placeholder: "Edit comment",
				}),
			],
		},
		[projectComment.uuid],
	)

	const isEditing = useEditorEditable(editor)

	const handleEditClicked = useCallback(() => {
		editor.setEditable(true, true)
		editor.chain().focus().run()
	}, [editor])

	const handleDelete = useCallback(
		(projectComment: Stored<ProjectComment>) => {
			openConfirmDeleteDialog({
				title: "Delete comment?",
				description: "Are you sure you want to delete this comment? This cannot be undone.",
				onAction: () => sdk.projectComments.delete(projectComment.uuid),
			})
		},
		[openConfirmDeleteDialog],
	)

	const handleResolvedChange = useCallback((projectComment: Stored<ProjectComment>, resolved: boolean) => {
		void sdk.projectComments.update({
			uuid: projectComment.uuid,
			resolved,
		})
	}, [])

	const handleSubmit = useCallback(() => {
		void sdk.projectComments.update({
			uuid: projectComment.uuid,
			content: JSON.stringify(editor?.getJSON()),
		})
		editor.setEditable(false, true)
		editor.chain().blur().run()
	}, [editor, projectComment.uuid])

	const handleCancel = useCallback(() => {
		editor.commands.setContent(editorContent)
		editor.setEditable(false, true)
		editor.chain().blur().run()
	}, [editor, editorContent])

	const isResolved = projectComment.resolved

	return (
		<div className="flex w-full gap-4">
			<div className="flex shrink-0 flex-col items-center gap-2 overflow-hidden">
				<UserIcon userId={author?.id} size="sm" />
				<Separator orientation="vertical" size="full" className="min-h-0 grow" />
			</div>

			<Card className="flex h-max min-w-0 grow flex-col gap-3" size="sm">
				{!isEditing ? (
					<>
						<div className="flex items-center justify-between gap-2">
							<div className="flex h-max min-w-0 grow items-center gap-2">
								<Text size="sm" className="truncate whitespace-nowrap" accentColor="base">
									<Text highContrast>{getUserDisplayLabel(author)}</Text>
									&nbsp;commented&nbsp;&#8226;&nbsp;
									{formatCompactDate(projectComment.submitted_at)}
								</Text>
							</div>

							<ButtonGroup
								className="flex items-center justify-end gap-2"
								size="sm"
								variant="ghost"
								accentColor="base"
							>
								{isResolved ? (
									<Text size="sm" accentColor="base">
										Resolved
									</Text>
								) : null}
								<ProjectCommentDropdownMenu
									variant="soft"
									accentColor="base"
									size="sm"
									trigger={
										<IconButton>
											<LuIcon icon={modelIconMapping.more} />
										</IconButton>
									}
									projectComment={projectComment}
									onResolvedChange={handleResolvedChange}
									onEdit={handleEditClicked}
									onDelete={handleDelete}
								/>
							</ButtonGroup>
						</div>

						{!isResolved && <Separator size="full" />}
					</>
				) : null}

				{(!isResolved || isEditing) && (
					<EditorContext value={editor}>
						<EditorContent editor={editor} className={cx("min-h-sm h-max", textSizeCva({ size: "sm" }))} />
						<TextBubbleMenu editor={editor} />
					</EditorContext>
				)}

				{isEditing && (
					<ButtonGroup className="flex h-max w-full justify-end gap-2" size="sm" variant="soft">
						<Button accentColor="base" onClick={handleCancel}>
							<LuIcon icon={modelIconMapping.close} />
							Cancel
						</Button>
						<Button onClick={handleSubmit} variant="surface">
							<LuIcon icon={modelIconMapping.check} />
							Submit
						</Button>
					</ButtonGroup>
				)}
			</Card>
		</div>
	)
})
ProjectCommentTimelineItem.displayName = "ProjectCommentTimelineItem"
